/**
 * @fileoverview Accessibility props for a selectable option item.
 * @module screen-view-v2/utils/optionItemA11y
 *
 * D-T9b-14: the WP8 UX review found every pick option rendered as a bare `div` —
 * `tabIndex = -1`, no `role`, no `aria-checked` — so a keyboard-only or switch user could
 * not answer a single question screen (0 of 9/11/5 options reachable by Tab).
 *
 * All 19 `OptionsItem` variants render their own root element. This builder is the single
 * place that decides what that root's control identity is, so the 19 roots cannot drift.
 * Focus MOVEMENT between options (arrow keys) belongs to the container — see `OptionsList`.
 *
 * ## Why per-option `tabIndex = 0` rather than a roving tabindex
 *
 * The WAI-ARIA radiogroup pattern normally uses a roving tabindex: one tab stop for the
 * whole group, arrows to move within it. We deliberately keep every option in the tab
 * order and add arrows on top. Two reasons:
 *
 *  1. A funnel screen is not a form full of widgets — the options usually ARE the screen,
 *     and each one is a separate answer. Tabbing through them is the behaviour users of
 *     switch access and sequential-navigation tools actually get, and it is what the
 *     acceptance receipt measures ("`view-options.items.*` reachable by Tab", 0/9 today).
 *  2. A roving tabindex would leave that receipt reading 1 of 9 — technically conformant,
 *     but indistinguishable in the walk log from the regression it replaced.
 *
 * Arrow keys still work, so the roving-tabindex muscle memory is not lost, only the
 * single-tab-stop restriction.
 */
import type { KeyboardEvent } from 'react';

/** `OptionsList.selectionMode` values that matter for the control identity. */
export type OptionSelectionMode = 'one' | 'many' | 'browse' | (string & {});

export interface OptionItemA11yInput {
  /** `selectionMode !== 'browse'` — a browse list is decorative, not a control. */
  selectable: boolean;
  selected: boolean;
  /** Disabled by `maxSelectedItemCount`; stays focusable, refuses activation. */
  disabled?: boolean;
  selectionMode?: OptionSelectionMode;
  /** Invoked by Space / Enter. Same effect as a tap. */
  onActivate?: () => void;
}

export interface OptionItemA11yProps {
  role?: 'radio' | 'checkbox';
  tabIndex?: number;
  'aria-checked'?: boolean;
  'aria-disabled'?: boolean;
  onKeyDown?: (event: KeyboardEvent) => void;
}

/** Keys that activate a native radio/checkbox. `Spacebar` is the legacy IE/Edge name. */
const ACTIVATION_KEYS = new Set([' ', 'Spacebar', 'Enter']);

export function getOptionItemA11yProps(input: OptionItemA11yInput): OptionItemA11yProps {
  const { selectable, selected, disabled = false, selectionMode, onActivate } = input;

  if (!selectable) {
    return {};
  }

  const props: OptionItemA11yProps = {
    role: selectionMode === 'many' ? 'checkbox' : 'radio',
    tabIndex: 0,
    'aria-checked': selected,
  };

  if (disabled) {
    props['aria-disabled'] = true;
  }

  props.onKeyDown = (event: KeyboardEvent) => {
    if (!ACTIVATION_KEYS.has(event.key)) return;
    // Space scrolls the page and Enter submits a form unless we claim the key.
    event.preventDefault();
    if (disabled) return;
    onActivate?.();
  };

  return props;
}
